import { ModelTier } from './ChatBoxInput';
import { ProjectAgentPreference } from '../../services/projectService';

export type AgentItem = {
  id: string;
  label: string;
  description: string;
  source: string;
  accent: string;
};

export type AgentGroup = {
  id: string;
  title: string;
  subtitle: string;
  agents: AgentItem[];
};

export type AgentPreference = ProjectAgentPreference;

export type ProjectAgentCacheEntry = {
  agents: Record<string, AgentPreference>;
  version: number;
};

export const LOCKED_AGENT_IDS = new Set([
  'branch_preparation_agent',
  'supervisor_agent',
  'implementation_agent',
  'verification_investigator',
  'decision_agent',
  'deployment_phase_6_agent',
  'docker_health_check_agent',
  'smart_command_recovery_agent',
]);

export const AGENT_GROUPS: AgentGroup[] = [
  {
    id: 'execution',
    title: 'Execution Agents',
    subtitle: 'Derived from runtime command types and verification/build flows.',
    agents: [
      {
        id: 'branch_preparation_agent',
        label: 'Branch Preparation Agent',
        description: 'Owns branch creation, sync, and Git preparation before the implementation phase begins.',
        source: 'BRANCH_PREPARE_REQUESTED phase',
        accent: '#84cc16',
      },
      {
        id: 'supervisor_agent',
        label: 'Supervisor Agent',
        description: 'Shapes the high-level task description prompt and decides which downstream execution agents should run.',
        source: 'supervisor planning and routing prompt',
        accent: '#f59e0b',
      },
      {
        id: 'implementation_agent',
        label: 'Implementation Agent',
        description: 'Handles the main implementation and coding work.',
        source: 'command_type="Implementation"',
        accent: '#4338ca',
      },
      {
        id: 'backend_quality_agent',
        label: 'Backend Quality Agent',
        description: 'Checks backend quality alignment and correctness expectations.',
        source: 'command_type="Backend Quality Alignment"',
        accent: '#7c3aed',
      },
      {
        id: 'frontend_quality_agent',
        label: 'Frontend Quality Agent',
        description: 'Checks frontend quality alignment and UI implementation quality.',
        source: 'command_type="Frontend Quality Alignment"',
        accent: '#e11d48',
      },
      {
        id: 'browser_verification_agent',
        label: 'Browser Verification Agent',
        description: 'Performs browser-level verification and visual workflow checks.',
        source: 'command_type="Browser Verification"',
        accent: '#0284c7',
      },
      {
        id: 'verification_investigator',
        label: 'Verification Investigator',
        description: 'Investigates why verification failed and gathers failure evidence.',
        source: 'command_type="Verification Investigator"',
        accent: '#ea580c',
      },
      {
        id: 'decision_agent',
        label: 'Decision Agent',
        description: 'Evaluates verification output to decide whether work is complete.',
        source: 'verification decision flow',
        accent: '#65a30d',
      },
      {
        id: 'test_agent',
        label: 'Test Agent',
        description: 'Controls the automated testing phase, including integration and browser verification gates.',
        source: 'Phase 5/6 testing pipeline',
        accent: '#0f766e',
      },
      {
        id: 'deployment_phase_6_agent',
        label: 'Deployment Agent',
        description: 'Controls deployment execution and rollout checks for phase 6.',
        source: 'Phase 6/6 deployment pipeline',
        accent: '#b45309',
      },
      {
        id: 'docker_health_check_agent',
        label: 'Docker Health Check Agent',
        description: 'Checks container-level health and runtime readiness.',
        source: 'command_type="docker_health_check"',
        accent: '#0f766e',
      },
      {
        id: 'smart_command_recovery_agent',
        label: 'Smart Command Recovery Agent',
        description: 'Retries and recovers from failed command execution flows.',
        source: 'command_type prefix="Smart Command Recovery"',
        accent: '#7c3aed',
      },
    ],
  },
];

export const FIXED_MODEL_OPTIONS: Partial<Record<string, ModelTier>> = {
  browser_verification_agent: 'super_large',
  verification_investigator: 'large',
  decision_agent: 'large',
};

export const HIDDEN_MODEL_DROPDOWN_AGENT_IDS = new Set([
  'supervisor_agent',
  'docker_health_check_agent',
  'deployment_phase_6_agent',
]);

export const HIGH_CAPABILITY_MODEL_OPTIONS: ModelTier[] = ['large', 'super_large'];
export const ALL_MODEL_OPTIONS: ModelTier[] = ['fast', 'spark', 'standard', 'large', 'super_large'];
